/**
 * Retelling 复述练习页面
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ShadowMaterial,
  VideoBlob,
  RetellingAnalysis,
  getMaterial,
  getVideoBlob,
  addRetellingRecord,
} from '../../../db/shadowDb';
import { useRecorder } from '../../hooks/useRecorder';
import { analyzeRetelling } from '../services/retellingAnalyzer';
import { VideoPlayer, VideoPlayerRef } from '../components/VideoPlayer';
import { RetellingResult } from '../components/RetellingResult';
import { VocabSuggestion } from '../components/VocabSuggestion';
import '../styles/shadowReading.css';

type PageState = 'loading' | 'ready' | 'watching' | 'recording' | 'analyzing' | 'result';

export function RetellingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 数据状态
  const [material, setMaterial] = useState<ShadowMaterial | null>(null);
  const [videoBlob, setVideoBlob] = useState<VideoBlob | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);

  // 结果状态
  const [analysis, setAnalysis] = useState<RetellingAnalysis | null>(null);
  const [recordingDataUrl, setRecordingDataUrl] = useState<string | null>(null);

  // Refs
  const videoPlayerRef = useRef<VideoPlayerRef>(null);

  // 录音 Hook
  const {
    state: recorderState,
    startRecording,
    stopRecording,
    isRecording,
  } = useRecorder({
    onRecordingComplete: async (result) => {
      setRecordingDataUrl(result.dataUrl);
      setPageState('analyzing');

      try {
        if (!material) throw new Error('素材不存在');

        const analysisResult = await analyzeRetelling(
          material.subtitles,
          result.blob,
          result.mimeType
        );
        setAnalysis(analysisResult);

        // 保存记录
        await addRetellingRecord(material.id, {
          recordingDataUrl: result.dataUrl,
          transcript: '', // 可以从分析中提取
          analysis: analysisResult,
        });

        // 重新加载素材
        const updated = await getMaterial(material.id);
        if (updated) {
          setMaterial(updated);
        }

        setPageState('result');
      } catch (err) {
        console.error('Analysis failed:', err);
        setError((err as Error).message);
        setPageState('ready');
      }
    },
    onError: (err) => {
      console.error('Recording error:', err);
      setError(err);
      setPageState('ready');
    },
    maxDurationMs: 180000, // 最长 3 分钟
  });

  // 加载素材
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    (async () => {
      try {
        const mat = await getMaterial(id);
        if (!mat) {
          throw new Error('素材不存在');
        }
        if (cancelled) return;
        setMaterial(mat);

        // 加载视频
        if (mat.videoBlobId) {
          const blob = await getVideoBlob(mat.videoBlobId);
          if (!cancelled && blob) {
            setVideoBlob(blob);
          }
        }

        setPageState('ready');
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // 流利度检查（软性门槛）
  const fluencyOk = material?.progress.overallFluency !== undefined
    ? material.progress.overallFluency >= 6.0
    : false;
  const practicePercent = material
    ? (material.progress.practicedSentences.length / material.subtitles.length) * 100
    : 0;

  // 播放完整视频
  const handlePlayVideo = useCallback(() => {
    if (videoPlayerRef.current) {
      setPageState('watching');
      videoPlayerRef.current.seekTo(0);
      videoPlayerRef.current.play();
    }
  }, []);

  // 开始录音
  const handleStartRecording = useCallback(() => {
    setPageState('recording');
    startRecording();
  }, [startRecording]);

  // 停止录音
  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // 重新录制
  const handleRetry = useCallback(() => {
    setAnalysis(null);
    setRecordingDataUrl(null);
    setPageState('ready');
  }, []);

  // 完成
  const handleFinish = useCallback(() => {
    navigate('/speaking/shadow');
  }, [navigate]);

  // 返回
  const handleBack = useCallback(() => {
    navigate('/speaking/shadow');
  }, [navigate]);

  // 添加词汇卡
  const handleAddVocab = useCallback((vocab: RetellingAnalysis['suggestedVocab'][0]) => {
    // TODO: 整合到现有词汇系统
    console.log('Add vocab:', vocab);
    alert(`词汇 "${vocab.word}" 已添加到待实现的词汇卡功能`);
  }, []);

  if (pageState === 'loading') {
    return (
      <div className="retelling-page">
        <div className="shadow-loading">
          <div className="loading-spinner"><span></span></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error && !material) {
    return (
      <div className="retelling-page">
        <div className="shadow-header">
          <button type="button" className="back-btn" onClick={handleBack}>←</button>
          <h2 className="page-title">错误</h2>
        </div>
        <div className="retelling-content">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="retelling-page">
      {/* 头部 */}
      <div className="shadow-header">
        <button type="button" className="back-btn" onClick={handleBack}>←</button>
        <h2 className="page-title">RETELLING</h2>
      </div>

      <div className="retelling-content">
        {/* 素材信息 */}
        {material && (
          <div className="retelling-instruction">
            <h3>{material.title}</h3>
            <p>
              练习进度: {practicePercent.toFixed(0)}%
              {material.progress.overallFluency !== undefined && (
                <> · 流利度: {material.progress.overallFluency.toFixed(1)}</>
              )}
            </p>
            {!fluencyOk && practicePercent < 50 && (
              <p className="warning-text">
                ⚠️ 建议先完成更多影子跟读练习再进行 Retelling
              </p>
            )}
          </div>
        )}

        {/* 视频播放器 */}
        {videoBlob && pageState !== 'result' && (
          <VideoPlayer
            ref={videoPlayerRef}
            videoBlob={videoBlob}
          />
        )}

        {/* 准备阶段 */}
        {pageState === 'ready' && (
          <div className="retelling-actions">
            <button type="button" className="action-btn primary" onClick={handlePlayVideo}>
              ▶ 播放完整视频
            </button>
            <p className="action-hint">观看视频后，用自己的话复述内容</p>
          </div>
        )}

        {/* 观看阶段 */}
        {pageState === 'watching' && (
          <div className="retelling-actions">
            <button type="button" className="action-btn primary" onClick={handleStartRecording}>
              🎤 开始复述
            </button>
          </div>
        )}

        {/* 录音阶段 */}
        {pageState === 'recording' && (
          <div className="recording-section">
            <button
              type="button"
              className="record-btn recording"
              onClick={handleStopRecording}
            >
              🎤 {recorderState.elapsedSeconds}s - 点击停止
            </button>
            <p className="recording-hint">用自己的话复述刚才看到的内容</p>
          </div>
        )}

        {/* 分析阶段 */}
        {pageState === 'analyzing' && (
          <div className="shadow-loading">
            <div className="loading-spinner"><span></span></div>
            <p>AI 正在分析你的复述...</p>
          </div>
        )}

        {/* 结果阶段 */}
        {pageState === 'result' && analysis && (
          <>
            <RetellingResult
              analysis={analysis}
              onAddVocab={handleAddVocab}
            />

            <div className="retelling-footer">
              <button type="button" className="action-btn" onClick={handleRetry}>
                重新录制
              </button>
              <button type="button" className="action-btn primary" onClick={handleFinish}>
                完成
              </button>
            </div>
          </>
        )}

        {/* 错误提示 */}
        {error && pageState !== 'loading' && (
          <div className="upload-error">{error}</div>
        )}
      </div>
    </div>
  );
}
